import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface RuleDashboardSummary {
  readonly totalRules: number;
  readonly criticalRules: number;
  readonly pendingApprovals: number;
  readonly unresolvedConflicts: number;
  readonly totalDeployments: number;
  readonly totalExecutions: number;
  readonly byStatus: Record<string, number>;
  readonly byApprovalStatus: Record<string, number>;
  readonly byCategory: Record<string, number>;
}

export function M24RuleDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [summary, setSummary] = useState<RuleDashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: RuleDashboardSummary }>('/v1/rule-dashboard', { token })
      .then((r) => { if (alive) setSummary(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!summary) return <p>{t('m24.dashboard.loading')}</p>;
  return (
    <main aria-label="rule-dashboard">
      <h1>{t('m24.dashboard.title')}</h1>
      <section aria-label="dashboard-metrics" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        <article aria-label="total-rules"><h2>{t('m24.dashboard.totalRules')}</h2><p>{summary.totalRules}</p></article>
        <article aria-label="critical-rules"><h2>{t('m24.dashboard.criticalRules')}</h2><p>{summary.criticalRules}</p></article>
        <article aria-label="pending-approvals"><h2>{t('m24.dashboard.pendingApprovals')}</h2><p>{summary.pendingApprovals}</p></article>
        <article aria-label="unresolved-conflicts"><h2>{t('m24.dashboard.unresolvedConflicts')}</h2><p>{summary.unresolvedConflicts}</p></article>
        <article aria-label="total-deployments"><h2>{t('m24.dashboard.deployments')}</h2><p>{summary.totalDeployments}</p></article>
        <article aria-label="total-executions"><h2>{t('m24.dashboard.executions')}</h2><p>{summary.totalExecutions}</p></article>
      </section>
      <section aria-label="by-status">
        <h2>{t('m24.dashboard.byStatus')}</h2>
        <table aria-label="status-breakdown">
          <thead><tr><th>{t('m24.dashboard.colStatus')}</th><th>{t('m24.dashboard.colCount')}</th></tr></thead>
          <tbody>
            {Object.entries(summary.byStatus).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
      <section aria-label="by-approval-status">
        <h2>{t('m24.dashboard.byApprovalStatus')}</h2>
        <table aria-label="approval-status-breakdown">
          <thead><tr><th>{t('m24.dashboard.colApprovalStatus')}</th><th>{t('m24.dashboard.colCount')}</th></tr></thead>
          <tbody>
            {Object.entries(summary.byApprovalStatus).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
      <section aria-label="by-category">
        <h2>{t('m24.dashboard.byCategory')}</h2>
        <table aria-label="category-breakdown">
          <thead><tr><th>{t('m24.dashboard.colCategory')}</th><th>{t('m24.dashboard.colCount')}</th></tr></thead>
          <tbody>
            {Object.entries(summary.byCategory).map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
    </main>
  );
}
